// useClvLineMovement — "Beat the Line" board data source (CLV-2).
//
// Reads public.vw_clv_line_movement: per (fixture, 1X2 selection) the
// opening -> current Pinnacle line, sourced from fixture_market_odds_history
// (one provider, one book, real timestamped prices). drift_pp > 0 means the
// price firmed (probability shortened — money came in on that selection).
//
// HONEST by construction: the view is thin today (odds were fetched ~weekly,
// so only a handful of fixtures have a 2+ snapshot trajectory). It deepens
// automatically as op_odds_sweep_6h + op_clv_snapshot accrue snapshots 4x/day.
// We never fabricate movement — a fixture with one snapshot simply does not
// appear until it has a second.
//
// Authenticated SELECT only (the view is granted to authenticated). No RPC,
// no service-role, no edge function.

import { useCallback, useEffect, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface ClvMovementRow {
  readonly fixture_id: string;
  readonly selection: "home" | "draw" | "away" | string;
  readonly competition_id: string | null;
  readonly kickoff_at: string;
  readonly home_team: string | null;
  readonly away_team: string | null;
  readonly open_odds: number;
  readonly open_prob: number;
  readonly opened_at: string;
  readonly cur_odds: number;
  readonly cur_prob: number;
  readonly latest_at: string;
  readonly drift_pp: number;
  readonly n_snaps: number;
}

export interface ClvLineMovementResult {
  readonly rows: readonly ClvMovementRow[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

interface RawRow {
  fixture_id: string;
  selection: string;
  competition_id: string | null;
  kickoff_at: string;
  home_team: string | null;
  away_team: string | null;
  open_odds: number | string;
  open_prob: number | string;
  opened_at: string;
  cur_odds: number | string;
  cur_prob: number | string;
  latest_at: string;
  drift_pp: number | string;
  n_snaps: number | string;
}

function num(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function useClvLineMovement(): ClvLineMovementResult {
  const [rows, setRows] = useState<readonly ClvMovementRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = getBrowserClient();
      // vw_clv_line_movement is not in the generated db/types.ts yet, so we
      // route through an untyped client handle (same pattern as
      // useFairPriceOutputs for clv_consensus). TODO: regen types.
      const { data, error: qErr } = await (client as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            order: (
              c: string,
              o: { ascending: boolean },
            ) => { limit: (n: number) => Promise<{ data: unknown; error: { message: string } | null }> };
          };
        };
      })
        .from("vw_clv_line_movement")
        .select(
          "fixture_id, selection, competition_id, kickoff_at, home_team, away_team, open_odds, open_prob, opened_at, cur_odds, cur_prob, latest_at, drift_pp, n_snaps",
        )
        .order("kickoff_at", { ascending: true })
        .limit(500);
      if (qErr) throw new Error(qErr.message);
      const mapped: ClvMovementRow[] = ((data ?? []) as unknown as RawRow[]).map((r) => ({
        fixture_id: r.fixture_id,
        selection: r.selection,
        competition_id: r.competition_id,
        kickoff_at: r.kickoff_at,
        home_team: r.home_team,
        away_team: r.away_team,
        open_odds: num(r.open_odds),
        open_prob: num(r.open_prob),
        opened_at: r.opened_at,
        cur_odds: num(r.cur_odds),
        cur_prob: num(r.cur_prob),
        latest_at: r.latest_at,
        drift_pp: num(r.drift_pp),
        n_snaps: num(r.n_snaps),
      }));
      setRows(mapped);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  return { rows, loading, error, refetch: fetchRows };
}
